"use client";

import React, {Component, ReactNode} from "react";
import ErrorMessage from "@/components/errors/ErrorMessage";

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
    errorComponent?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: undefined };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        /*
         You could send this info to a logging service here
         */
        console.error("Uncaught error:", error, errorInfo);
        fetch("/api/errors/log", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                message: error.message,
                stack: error.stack,
                componentStack: errorInfo.componentStack,
                userAgent: navigator.userAgent,
                pathname: window.location.pathname,
                queryParams: window.location.search,
            }),
        });
    }

    resetErrorBoundary = () => {
        this.setState({ hasError: false, error: undefined });
    };

    render() {

        const { hasError, error } = this.state;
        const { children, fallback, errorComponent } = this.props;

        if (hasError) {

            if (errorComponent && error) {
                return errorComponent(error, this.resetErrorBoundary);
            }

            return fallback ?? <ErrorMessage action={this.resetErrorBoundary} />;

        }

        return children;

    }

}
